import { useState, useEffect, useCallback, useMemo } from 'react';
import { SlidersHorizontal, X, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { FILTER_DIMENSIONS, totalSelectedCount } from '@/lib/filters';
import { tagHaystack } from '@/lib/tagSearch';
import DimensionInfoButton from '@/components/DimensionInfoButton';

const SM_UP = '(min-width: 640px)';

function useIsSmUp() {
  const [isSmUp, setIsSmUp] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(SM_UP).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(SM_UP);
    const onChange = () => setIsSmUp(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isSmUp;
}

// Reusable filter UI — renders three things in order:
//   1. A "Filters (n)" toggle button + "Clear all" affordance.
//   2. A row of always-visible chips for each currently-selected tag,
//      so the user always sees what's narrowing without opening the panel.
//   3. Desktop (≥sm): expandable panel below. Mobile (<sm): same content in a bottom sheet.
//
// Fully controlled — the parent owns `selected` state and supplies callbacks.
// This lets the same component drive:
//   - LandingPage (selected = local React state, committed on submit)
//   - SearchResultsPage (selected = derived from URL searchParams)
//
// Props:
//   selected:      per-FILTER_DIMENSIONS key, e.g. { life_areas: Set, guna_arc: Set, ... }
//   tagOptions:    per-FILTER_DIMENSIONS key, e.g. { life_areas: [...], guna_arc: [...], ... }
//   onToggleTag:   (dimensionKey, tagId) => void   — toggles one tag
//   onClearAll:    () => void                      — clears all selections
//   defaultOpen:   boolean (optional, default false)
//   compact:       boolean (optional)              — tighter spacing for hero
//   panelCompact:  boolean (optional)              — shorter scroll panel (search results page)
//   searchForm:    React node (optional)           — when set: filters column left, slot right (toolbar row)
//   align:         'left' | 'center' — chip rows only (toolbar is always start-aligned on wide screens)
//   matchSummary:  optional short string (e.g. "12 stories", "Searching…") — search page passes live counts
export default function SearchFilters({
  selected,
  tagOptions,
  onToggleTag,
  onClearAll,
  defaultOpen = false,
  compact = false,
  panelCompact = false,
  searchForm = null,
  align = 'left',
  matchSummary,
}) {
  const isSmUp = useIsSmUp();
  const [desktopPanelOpen, setDesktopPanelOpen] = useState(defaultOpen);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  // Typeahead — narrows visible chips across every dimension as the user
  // types. Empty string = show everything (the default browse state).
  const [filterQuery, setFilterQuery] = useState('');

  useEffect(() => {
    if (isSmUp) setMobileSheetOpen(false);
  }, [isSmUp]);

  useEffect(() => {
    if (!isSmUp && defaultOpen) setMobileSheetOpen(true);
  }, [defaultOpen, isSmUp]);

  // Reset the typeahead query whenever the panel closes — opening fresh
  // shouldn't carry over a stale "shamb" filter from last time.
  const panelOpen = isSmUp ? desktopPanelOpen : mobileSheetOpen;
  useEffect(() => {
    if (!panelOpen) setFilterQuery('');
  }, [panelOpen]);

  const total = totalSelectedCount(selected);
  const rowJustify = align === 'center' ? 'justify-center' : '';
  const chipsJustify = align === 'center' ? 'justify-center' : '';
  const showMatchSummary = typeof matchSummary === 'string' && matchSummary.length > 0;

  const tagLabel = useCallback(
    (key, tagId) => {
      const found = (tagOptions[key] || []).find((t) => t.id === tagId);
      return found?.display_name || tagId;
    },
    [tagOptions]
  );

  const activeChips = FILTER_DIMENSIONS.flatMap(({ key }) =>
    Array.from(selected[key] || []).map((id) => ({ key, id, label: tagLabel(key, id) }))
  );

  const toggleFiltersClick = () => {
    if (isSmUp) setDesktopPanelOpen((v) => !v);
    else setMobileSheetOpen(true);
  };

  const controls = (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={toggleFiltersClick}
        className="border-[#E6E2D6] text-[#2D2A26] text-[15px] sm:text-base"
        data-testid="toggle-filters-btn"
        aria-expanded={isSmUp ? desktopPanelOpen : mobileSheetOpen}
      >
        <SlidersHorizontal className="h-4 w-4 mr-2 shrink-0" />
        Filters
        {total > 0 && (
          <span className="ml-2 px-2 py-0.5 text-xs bg-[#C05621] text-white rounded-full">
            {total}
          </span>
        )}
      </Button>
      {total > 0 && (
        <Button
          type="button"
          variant="ghost"
          onClick={onClearAll}
          className="text-[#6B665E] hover:text-[#C05621]"
          data-testid="clear-filters-btn"
        >
          <X className="h-4 w-4 mr-1 shrink-0" />
          Clear all
        </Button>
      )}
    </>
  );

  const panelScrollClasses = panelCompact
    ? 'max-h-[min(240px,34vh)] sm:max-h-[min(300px,40vh)] p-4 space-y-4'
    : 'max-h-[55vh] p-6 space-y-5';

  const mobileSheetScrollClasses = panelCompact
    ? 'max-h-[min(52vh,420px)] overflow-y-auto px-4 pb-2 space-y-4'
    : 'max-h-[min(60vh,520px)] overflow-y-auto px-4 pb-2 space-y-5';

  /** ~14px tags: matches pre-toolbar reference legibility without oversized chips. */
  const tagChipClasses =
    'rounded-full border transition-colors text-sm leading-normal px-3 py-1.5';

  const normalizedQuery = filterQuery.trim().toLowerCase();
  const queryActive = normalizedQuery.length > 0;

  // Build the per-dimension match lists once per (query, options) change.
  // Selected chips always pass the filter — hiding a tag you just selected
  // would feel like a bug ("where did my pick go?").
  const filteredByKey = useMemo(() => {
    const out = {};
    for (const { key } of FILTER_DIMENSIONS) {
      const options = tagOptions[key] || [];
      out[key] = !queryActive
        ? options
        : options.filter((tag) => {
            if (selected[key]?.has(tag.id)) return true;
            return tagHaystack(tag).includes(normalizedQuery);
          });
    }
    return out;
  }, [tagOptions, normalizedQuery, queryActive, selected]);

  const totalMatchCount = useMemo(
    () => Object.values(filteredByKey).reduce((sum, arr) => sum + arr.length, 0),
    [filteredByKey],
  );
  const noMatches = queryActive && totalMatchCount === 0;

  const searchBar = (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B665E] pointer-events-none" />
      <Input
        type="search"
        value={filterQuery}
        onChange={(e) => setFilterQuery(e.target.value)}
        placeholder="Find a tag — try &ldquo;Restless&rdquo;, &ldquo;seva&rdquo;, &ldquo;aparigraha&rdquo;…"
        className="pl-9 pr-9 border-[#E6E2D6] bg-white"
        data-testid="filter-typeahead-input"
        aria-label="Find a tag"
      />
      {filterQuery && (
        <button
          type="button"
          onClick={() => setFilterQuery('')}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#6B665E] hover:text-[#C05621] hover:bg-[#FBF4EB]"
          data-testid="filter-typeahead-clear"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );

  const dimensionSections = FILTER_DIMENSIONS.map(({ key, label, emoji }) => {
    const options = tagOptions[key] || [];
    if (!options.length) return null;
    const matches = filteredByKey[key] || [];
    // When typing, hide whole dimensions that have no matches — keeps the
    // panel from feeling like a wall of "no results" subheads.
    if (queryActive && matches.length === 0) return null;
    return (
      <div key={key}>
        <h4 className="text-sm font-semibold text-[#2D2A26] mb-2 inline-flex items-center gap-1.5">
          {emoji ? <span aria-hidden>{emoji}</span> : null}
          {label}
          <DimensionInfoButton dimensionKey={key} />
        </h4>
        <div className="flex flex-wrap gap-2">
          {matches.map((tag) => {
            const isOn = selected[key]?.has(tag.id);
            return (
              <button
                key={`${tag.dimension}-${tag.id}`}
                type="button"
                onClick={() => onToggleTag(key, tag.id)}
                className={
                  tagChipClasses +
                  ' ' +
                  (isOn
                    ? 'bg-[#C05621] text-white border-[#C05621]'
                    : 'bg-[#FDFBF7] text-[#2D2A26] border-[#E6E2D6] hover:border-[#C05621]')
                }
                data-testid={`filter-${key}-${tag.id}`}
              >
                {tag.display_name}
              </button>
            );
          })}
        </div>
      </div>
    );
  });

  const emptyState = (
    <p
      className="text-sm text-[#6B665E] italic"
      data-testid="filter-typeahead-empty"
    >
      No tags match &ldquo;{filterQuery}&rdquo;. Try a different word.
    </p>
  );

  // Active-chips pill row. Rendered in two places:
  //   • Outside the panel as a summary when the panel is closed.
  //   • Inside the panel just under the tag search, when the panel is
  //     open — gives the user a one-click clear surface right next to
  //     where they're adding/searching tags.
  // Both render the same buttons; same onToggleTag, same accessibility.
  const renderActiveChips = (testId) =>
    activeChips.length > 0 ? (
      <div className={'flex flex-wrap gap-2 ' + chipsJustify} data-testid={testId}>
        {activeChips.map(({ key, id, label }) => (
          <button
            key={`${key}:${id}`}
            type="button"
            onClick={() => onToggleTag(key, id)}
            className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1 text-sm rounded-full bg-[#C05621] text-white hover:bg-[#A0461A] leading-normal"
            data-testid={`active-chip-${key}-${id}`}
          >
            {label}
            <X className="h-3 w-3 shrink-0" />
          </button>
        ))}
      </div>
    ) : null;

  // Wrapped variant for in-panel use, with a small heading so the user
  // knows what the row represents (vs the dimension chips below it).
  const inPanelSelectedChips = activeChips.length > 0 ? (
    <div
      className="pb-4 mb-1 border-b border-[#E6E2D6]/70"
      data-testid="in-panel-selected-chips"
    >
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs uppercase tracking-wide text-[#6B665E] font-medium">
          Selected ({activeChips.length})
        </h4>
        <button
          type="button"
          onClick={onClearAll}
          className="text-xs text-[#6B665E] hover:text-[#C05621] underline-offset-2 hover:underline"
          data-testid="in-panel-clear-all"
        >
          Clear all
        </button>
      </div>
      {renderActiveChips('in-panel-active-chips')}
    </div>
  ) : null;

  return (
    <div className={(compact ? 'space-y-3' : 'space-y-4') + ' w-full'}>
      {searchForm ? (
        <div
          className={
            'flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 w-full ' +
            (align === 'center' ? 'sm:justify-center' : '')
          }
        >
          <div
            className={
              'flex items-center gap-x-3 gap-y-1 flex-wrap shrink-0 sm:pt-3.5 ' +
              (align === 'center' ? 'justify-center sm:justify-start' : '')
            }
          >
            {controls}
            {showMatchSummary && (
              <span
                className="text-sm text-[#6B665E] whitespace-nowrap"
                data-testid="filter-match-summary"
              >
                {matchSummary}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0 flex flex-col gap-4 w-full">{searchForm}</div>
        </div>
      ) : (
        <div className={'flex items-center gap-x-3 gap-y-1 flex-wrap ' + rowJustify}>
          {controls}
          {showMatchSummary && (
            <span
              className="text-sm text-[#6B665E] whitespace-nowrap"
              data-testid="filter-match-summary"
            >
              {matchSummary}
            </span>
          )}
        </div>
      )}

      {/* Outside-panel summary chips: shown only when the panel is
          closed. When the panel is open, the same chips appear inside
          (just under the tag search), so the user always sees their
          selection close to whatever they're doing. */}
      {!panelOpen && renderActiveChips('active-filter-chips')}

      {isSmUp && desktopPanelOpen && (
        <div
          className={
            'bg-white rounded-3xl border border-[#E6E2D6] shadow-lg overflow-y-auto text-left ' +
            panelScrollClasses
          }
          data-testid="filter-panel"
        >
          {panelCompact && showMatchSummary ? (
            <p className="text-sm text-[#6B665E] mb-3 pb-3 border-b border-[#E6E2D6]/70">
              {matchSummary}
            </p>
          ) : null}
          <div className="mb-4">{searchBar}</div>
          {inPanelSelectedChips}
          {noMatches ? emptyState : dimensionSections}
        </div>
      )}

      {!isSmUp && (
        <Sheet open={mobileSheetOpen} onOpenChange={setMobileSheetOpen}>
          <SheetContent
            side="bottom"
            className="max-h-[90vh] rounded-t-2xl border-[#E6E2D6] bg-[#FDFBF7] p-0 gap-0"
            data-testid="filter-panel-sheet"
          >
            <SheetHeader className="border-b border-[#E6E2D6] px-4 pb-3 pt-2 text-left space-y-1">
              <SheetTitle className="font-serif text-[#2D2A26]">Filters</SheetTitle>
              {showMatchSummary ? (
                <SheetDescription className="text-left text-[#6B665E] text-sm font-normal">
                  {matchSummary}
                </SheetDescription>
              ) : null}
            </SheetHeader>
            <div className={mobileSheetScrollClasses} data-testid="filter-panel">
              <div className="pt-3">{searchBar}</div>
              {inPanelSelectedChips}
              {noMatches ? emptyState : dimensionSections}
            </div>
            <SheetFooter className="border-t border-[#E6E2D6] p-4 w-full">
              <SheetClose asChild>
                <Button
                  type="button"
                  className="w-full rounded-full bg-[#C05621] hover:bg-[#A0461A] text-white"
                  data-testid="filter-sheet-done-btn"
                >
                  Done
                </Button>
              </SheetClose>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
